import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, X, RotateCcw, ChevronRight, Lightbulb, BookOpen, Timer, Award } from "lucide-react";
import type { Exercise, FretPosition, UserProgress } from "../../lib/gym/types";
import { getCategoryLabel, getDifficultyLabel, getTypeLabel } from "../../lib/gym/trainingPlan";
import { formatPracticeDuration } from "../../lib/gym/progressMetrics";
import { learnSurface } from "../../lib/learnSurfaceClasses";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { GuitarNeck } from "../GuitarNeck";
import {
  fretPositionsToNeck,
  gymMutedStrings,
} from "../../lib/gym/fretPositionAdapter";
import { findMatchingPosition, noteOctaveToMidi } from "../../lib/gym/gymAudioMatch";
import { GYM_STEP_ADVANCE_TOTAL_MS } from "../../lib/gym/gymTiming";
import type { NoteName } from "../../lib/guitarEngine";

type ExerciseMode = "intro" | "playing" | "completed";

interface GymSessionContextValue {
  exercise: Exercise;
  progress: UserProgress;
  mode: ExerciseMode;
  currentStepIdx: number;
  currentStep: Exercise["steps"][number] | undefined;
  totalSteps: number;
  isLastStep: boolean;
  selectedPositions: FretPosition[];
  score: number;
  attempts: number;
  showHint: boolean;
  showAnswers: boolean;
  isCorrect: boolean | null;
  quizPicked: Set<string>;
  wasCompletedBefore: boolean;
  handleStart: () => void;
  handleFretClick: (stringNum: number, fret: number) => void;
  handleAudioNote: (note: NoteName, octave: number) => void;
  stepLocked: boolean;
  ripplingStrings: number[];
  usesAudioInput: boolean;
  handleQuizOption: (optionValue: string) => void;
  handleRestart: () => void;
  handleReveal: () => void;
  setShowHint: (v: boolean | ((b: boolean) => boolean)) => void;
  /** Segundos desde "Comenzar" en el paso actual (solo reproducción). */
  sessionElapsedSec: number;
}

const GymSessionContext = createContext<GymSessionContextValue | null>(null);

export function useGymSession() {
  const ctx = useContext(GymSessionContext);
  if (!ctx) throw new Error("useGymSession must be used within GymExerciseProvider");
  return ctx;
}

export interface GymExerciseProviderProps {
  exercise: Exercise;
  progress: UserProgress;
  onComplete: (exerciseId: string, score: number) => void;
  /** Suma segundos reales de práctica (desde Comenzar hasta completar / salir). */
  onAccumulatePracticeSeconds?: (seconds: number) => void;
  /** Desde Camino principiante: fuerza mic en practice/sequence. */
  pathMode?: boolean;
  children: ReactNode;
}

export function GymExerciseProvider({
  exercise,
  progress,
  onComplete,
  onAccumulatePracticeSeconds,
  pathMode = false,
  children,
}: GymExerciseProviderProps) {
  const [mode, setMode] = useState<ExerciseMode>("intro");
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [selectedPositions, setSelectedPositions] = useState<FretPosition[]>([]);
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [quizPicked, setQuizPicked] = useState<Set<string>>(() => new Set());
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [stepLocked, setStepLocked] = useState(false);
  const [ripplingStrings, setRipplingStrings] = useState<number[]>([]);
  const wrongAudioCooldownRef = useRef(0);

  const scoreRef = useRef(0);
  const attemptsRef = useRef(0);
  const sessionStartRef = useRef<number | null>(null);
  const accumulateRef = useRef(onAccumulatePracticeSeconds);
  accumulateRef.current = onAccumulatePracticeSeconds;
  scoreRef.current = score;
  attemptsRef.current = attempts;

  const flushPracticeSession = useCallback(() => {
    if (sessionStartRef.current == null) return;
    const secs = Math.max(0, Math.floor((Date.now() - sessionStartRef.current) / 1000));
    sessionStartRef.current = null;
    if (secs > 0) accumulateRef.current?.(secs);
  }, []);

  useEffect(() => () => flushPracticeSession(), [flushPracticeSession]);

  useEffect(() => {
    if (mode !== "playing") return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [mode]);

  const currentStep = exercise.steps[currentStepIdx];
  const totalSteps = exercise.steps.length;
  const isLastStep = currentStepIdx === totalSteps - 1;
  const wasCompletedBefore = progress.completedExercises.includes(exercise.id);

  const stepHasQuiz = Boolean(currentStep?.options?.length);
  const stepHasFrets = Boolean(currentStep?.positions?.length);
  const hasNeckTargets = exercise.steps.some((s) => (s.positions?.length ?? 0) > 0);
  /** Mic vía usePitchDetector (GymExerciseStudio → useGymAudioListener). */
  const usesAudioInput = useMemo(() => {
    if (!hasNeckTargets) return false;
    if (pathMode && (exercise.type === "practice" || exercise.type === "sequence")) {
      return true;
    }
    if (exercise.type === "practice" || exercise.type === "sequence") return true;
    // click/quiz con posiciones: también mic (mástil no interactivo en studio)
    return true;
  }, [hasNeckTargets, pathMode, exercise.type]);

  const sessionElapsedSec =
    mode === "playing" && sessionStartRef.current != null
      ? Math.max(0, Math.floor((nowTick - sessionStartRef.current) / 1000))
      : 0;

  useEffect(() => {
    setMode("intro");
    setCurrentStepIdx(0);
    setSelectedPositions([]);
    setScore(0);
    setShowHint(false);
    setShowAnswers(false);
    setIsCorrect(null);
    setAttempts(0);
    setQuizPicked(new Set());
    setStepLocked(false);
    setRipplingStrings([]);
    setNowTick(Date.now());
  }, [exercise.id]);

  const advanceAfterCorrect = useCallback(
    (deltaScore: number, celebrateString?: number) => {
      const nextScore = scoreRef.current + deltaScore;
      scoreRef.current = nextScore;
      setScore(nextScore);
      setIsCorrect(true);
      setStepLocked(true);
      if (celebrateString != null) {
        setRipplingStrings([celebrateString]);
      }
      window.setTimeout(() => {
        setRipplingStrings([]);
        if (isLastStep) {
          flushPracticeSession();
          setMode("completed");
          onComplete(exercise.id, scoreRef.current);
        } else {
          setCurrentStepIdx((i) => i + 1);
          setSelectedPositions([]);
          setQuizPicked(new Set());
          setIsCorrect(null);
          setAttempts(0);
          attemptsRef.current = 0;
          setShowHint(false);
          setShowAnswers(false);
        }
        setStepLocked(false);
      }, GYM_STEP_ADVANCE_TOTAL_MS);
    },
    [exercise.id, isLastStep, onComplete, flushPracticeSession],
  );

  const handleStart = useCallback(() => {
    flushPracticeSession();
    sessionStartRef.current = Date.now();
    setNowTick(Date.now());
    setMode("playing");
    setCurrentStepIdx(0);
    setSelectedPositions([]);
    setScore(0);
    scoreRef.current = 0;
    setAttempts(0);
    attemptsRef.current = 0;
    setQuizPicked(new Set());
    setShowHint(false);
    setShowAnswers(false);
    setIsCorrect(null);
    setStepLocked(false);
    setRipplingStrings([]);
  }, [flushPracticeSession]);

  const handleAudioNote = useCallback(
    (note: NoteName, octave: number) => {
      if (mode !== "playing" || !currentStep || stepHasQuiz || stepLocked) return;
      if (!currentStep.positions?.length) return;

      const detectedMidi = noteOctaveToMidi(note, octave);
      const matchPos = findMatchingPosition(detectedMidi, currentStep.positions);
      if (!matchPos) {
        const now = Date.now();
        if (now - wrongAudioCooldownRef.current < 1_400) return;
        wrongAudioCooldownRef.current = now;
        setAttempts((a) => {
          const n = a + 1;
          attemptsRef.current = n;
          return n;
        });
        setIsCorrect(false);
        window.setTimeout(() => setIsCorrect(null), 650);
        return;
      }

      const already = selectedPositions.some(
        (p) => p.string === matchPos.string && p.fret === matchPos.fret,
      );
      if (already) return;

      const newSelected = [...selectedPositions, matchPos];
      setSelectedPositions(newSelected);
      const targetPositions = currentStep.positions;
      const allFound = targetPositions.every((tp) =>
        newSelected.some((sp) => sp.string === tp.string && sp.fret === tp.fret),
      );
      if (allFound) {
        const delta = Math.max(10 - attemptsRef.current, 5);
        advanceAfterCorrect(delta, matchPos.string);
      }
    },
    [
      mode,
      currentStep,
      stepHasQuiz,
      stepLocked,
      selectedPositions,
      advanceAfterCorrect,
    ],
  );

  const handleFretClick = useCallback(
    (stringNum: number, fret: number) => {
      if (usesAudioInput || mode !== "playing" || !currentStep || stepHasQuiz || stepLocked) return;
      const newPos = { string: stringNum, fret };
      const types = exercise.type;
      if (types === "quiz" && !stepHasFrets) return;

      if (types === "click" || types === "sequence" || types === "practice" || types === "quiz") {
        const isMatch = currentStep.positions?.some((p) => p.string === stringNum && p.fret === fret);
        if (isMatch) {
          const already = selectedPositions.some((p) => p.string === stringNum && p.fret === fret);
          if (!already) {
            const newSelected = [...selectedPositions, newPos];
            setSelectedPositions(newSelected);
            const targetPositions = currentStep.positions || [];
            const allFound = targetPositions.every((tp) =>
              newSelected.some((sp) => sp.string === tp.string && sp.fret === tp.fret),
            );
            if (allFound) {
              const delta = Math.max(10 - attemptsRef.current, 5);
              advanceAfterCorrect(delta);
            }
          }
        } else {
          setAttempts((a) => {
            const n = a + 1;
            attemptsRef.current = n;
            return n;
          });
          setIsCorrect(false);
          window.setTimeout(() => setIsCorrect(null), 700);
        }
      }
    },
    [
      usesAudioInput,
      mode,
      currentStep,
      exercise.type,
      selectedPositions,
      stepHasQuiz,
      stepHasFrets,
      stepLocked,
      advanceAfterCorrect,
    ],
  );

  const handleQuizOption = useCallback(
    (optionValue: string) => {
      if (mode !== "playing" || !currentStep?.options) return;
      const opt = currentStep.options.find((o) => o.value === optionValue);
      if (!opt) return;

      if (!opt.correct) {
        setAttempts((a) => {
          const n = a + 1;
          attemptsRef.current = n;
          return n;
        });
        setIsCorrect(false);
        window.setTimeout(() => setIsCorrect(null), 700);
        return;
      }

      setQuizPicked((prev) => {
        const next = new Set(prev);
        if (next.has(optionValue)) next.delete(optionValue);
        else next.add(optionValue);

        const correctValues = currentStep.options!.filter((o) => o.correct).map((o) => o.value);
        const hasAll = correctValues.length > 0 && correctValues.every((v) => next.has(v));
        const onlyCorrect = [...next].every((v) => !!currentStep.options!.find((o) => o.value === v)?.correct);
        if (hasAll && onlyCorrect && next.size === correctValues.length) {
          window.queueMicrotask(() => {
            const delta = Math.max(10 - attemptsRef.current, 5);
            advanceAfterCorrect(delta);
          });
        }
        return next;
      });
    },
    [mode, currentStep, advanceAfterCorrect],
  );

  const handleRestart = useCallback(() => {
    flushPracticeSession();
    setMode("intro");
    setCurrentStepIdx(0);
    setSelectedPositions([]);
    setScore(0);
    scoreRef.current = 0;
    setShowHint(false);
    setShowAnswers(false);
    setIsCorrect(null);
    setAttempts(0);
    attemptsRef.current = 0;
    setQuizPicked(new Set());
    setStepLocked(false);
    setRipplingStrings([]);
  }, [flushPracticeSession]);

  const handleReveal = useCallback(() => setShowAnswers(true), []);

  const value = useMemo<GymSessionContextValue>(
    () => ({
      exercise,
      progress,
      mode,
      currentStepIdx,
      currentStep,
      totalSteps,
      isLastStep,
      selectedPositions,
      score,
      attempts,
      showHint,
      showAnswers,
      isCorrect,
      quizPicked,
      wasCompletedBefore,
      handleStart,
      handleFretClick,
      handleAudioNote,
      stepLocked,
      ripplingStrings,
      usesAudioInput,
      handleQuizOption,
      handleRestart,
      handleReveal,
      setShowHint,
      sessionElapsedSec,
    }),
    [
      exercise,
      progress,
      mode,
      currentStepIdx,
      currentStep,
      totalSteps,
      isLastStep,
      selectedPositions,
      score,
      attempts,
      showHint,
      showAnswers,
      isCorrect,
      quizPicked,
      wasCompletedBefore,
      handleStart,
      handleFretClick,
      handleAudioNote,
      stepLocked,
      ripplingStrings,
      usesAudioInput,
      handleQuizOption,
      handleRestart,
      handleReveal,
      sessionElapsedSec,
    ],
  );

  return <GymSessionContext.Provider value={value}>{children}</GymSessionContext.Provider>;
}

export function GymExerciseMain() {
  const {
    exercise,
    mode,
    currentStepIdx,
    currentStep,
    totalSteps,
    selectedPositions,
    score,
    attempts,
    showHint,
    showAnswers,
    isCorrect,
    quizPicked,
    wasCompletedBefore,
    handleStart,
    handleFretClick,
    handleQuizOption,
    handleRestart,
    handleReveal,
    setShowHint,
    sessionElapsedSec,
  } = useGymSession();

  const stepHasQuiz = Boolean(currentStep?.options?.length);
  const stepHasFrets = Boolean(currentStep?.positions?.length);
  const showQuizRow = mode === "playing" && stepHasQuiz;
  const fretInteractive =
    mode === "playing" &&
    !stepHasQuiz &&
    (exercise.type === "click" || exercise.type === "sequence" || exercise.type === "practice" || exercise.type === "quiz");
  const showFretboard = mode === "playing" && stepHasFrets && !stepHasQuiz;

  const visibleStrings = exercise.stringsNeeded || [1, 2, 3, 4, 5, 6];
  const fretRange = exercise.fretRange || [0, 15];

  const gymMuted = useMemo(() => gymMutedStrings(visibleStrings), [visibleStrings]);

  const neckHighlights = useMemo(() => {
    const out = [];
    if (showAnswers && currentStep?.positions) {
      out.push(...fretPositionsToNeck(currentStep.positions, "root"));
    }
    for (const sp of selectedPositions) {
      if (!out.some((p) => p.string === sp.string && p.fret === sp.fret)) {
        out.push(...fretPositionsToNeck([sp], "active"));
      }
    }
    return out;
  }, [showAnswers, currentStep?.positions, selectedPositions]);

  return (
    <div className="flex flex-col h-full min-h-0 text-slate-100 font-sans">
      <div className="px-4 py-3 border-b border-white/10 bg-black/25 shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span
                className={cn(
                  "text-[10px] px-1.5 py-0.5 rounded font-semibold",
                  exercise.difficulty === "facil" && "bg-emerald-500/20 text-emerald-200",
                  exercise.difficulty === "medio" && "bg-sky-500/20 text-sky-200",
                  exercise.difficulty === "dificil" && "bg-amber-500/20 text-amber-200",
                  exercise.difficulty === "experto" && "bg-red-500/20 text-red-200",
                )}
              >
                {getDifficultyLabel(exercise.difficulty)}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-medium">
                {getCategoryLabel(exercise.category)}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400 font-medium">
                {getTypeLabel(exercise.type)}
              </span>
            </div>
            <h2 className="text-lg font-bold text-white leading-tight">{exercise.title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{exercise.subtitle}</p>
          </div>
          {mode === "playing" && (
            <div className="flex items-center gap-3 shrink-0 text-right">
              <div>
                <p className="text-[9px] text-slate-500 uppercase">Paso</p>
                <p className="text-xs font-bold text-slate-200">
                  {currentStepIdx + 1}/{totalSteps}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase">Sesión</p>
                <p className="text-xs font-bold text-slate-200 tabular-nums">
                  {Math.floor(sessionElapsedSec / 60)}:{(sessionElapsedSec % 60).toString().padStart(2, "0")}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-slate-500 uppercase">Pts</p>
                <p className="text-xs font-bold text-[var(--bordona-accent)]">{score}</p>
              </div>
              {attempts > 0 && (
                <div>
                  <p className="text-[9px] text-slate-500 uppercase">Intentos</p>
                  <p className="text-xs font-bold text-red-300">{attempts}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {mode === "intro" ? (
          <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
            <div className={cn(learnSurface.panelLoose, "max-w-md w-full")}>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-[var(--bordona-accent)]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Descripcion</span>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed mb-3">{exercise.description}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Instrucciones</p>
              <ul className="space-y-1.5 mb-3">
                {exercise.instructions.map((inst, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                    <span className="w-5 h-5 rounded-full bg-white/10 text-slate-200 flex items-center justify-center text-[10px] font-bold shrink-0">
                      {i + 1}
                    </span>
                    {inst}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 text-[10px] text-slate-500 mb-4">
                <span className="flex items-center gap-1">
                  <Timer className="w-3 h-3" />~{exercise.timeEstimate} min
                </span>
                <span className="truncate">Ref: {exercise.methodRef}</span>
              </div>
              <Button
                onClick={handleStart}
                className="w-full bg-[var(--bordona-accent)] hover:opacity-95 text-white font-semibold"
                size="lg"
              >
                {wasCompletedBefore ? "Repetir ejercicio" : "Comenzar ejercicio"}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : mode === "completed" ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
              <div className="w-14 h-14 rounded-full bg-emerald-500/30 flex items-center justify-center mx-auto mb-3 border border-emerald-400/40">
                <Award className="w-7 h-7 text-emerald-200" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">Ejercicio completado</h3>
              <p className="text-sm text-slate-400 mb-3">{exercise.title}</p>
              <div className="flex items-center justify-center gap-3 my-3">
                <div className={cn(learnSurface.panel, "px-3 py-2")}>
                  <p className="text-[9px] text-slate-500 uppercase">Puntuacion</p>
                  <p className="text-xl font-bold text-[var(--bordona-accent)]">{score}</p>
                </div>
                <div className={cn(learnSurface.panel, "px-3 py-2")}>
                  <p className="text-[9px] text-slate-500 uppercase">Pasos</p>
                  <p className="text-xl font-bold text-sky-300">
                    {totalSteps}/{totalSteps}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 italic mb-4">{exercise.professorNote}</p>
              <Button variant="outline" onClick={handleRestart} className="border-white/20 bg-white/5 text-slate-100">
                <RotateCcw className="w-4 h-4" />
                Repetir
              </Button>
            </div>
          </div>
        ) : (
          <>
            {currentStep && (
              <div className="px-4 py-2.5 bg-black/20 border-b border-white/10 shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-white/15 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                      {currentStepIdx + 1}
                    </span>
                    <p className="text-xs font-medium text-slate-100 leading-snug">{currentStep.instruction}</p>
                  </div>
                  <div className="shrink-0">
                    {isCorrect === true && (
                      <span className="flex items-center gap-1 text-emerald-300 text-[11px] font-semibold">
                        <Check className="w-3.5 h-3.5" /> Listo
                      </span>
                    )}
                    {isCorrect === false && (
                      <span className="flex items-center gap-1 text-red-300 text-[11px] font-semibold">
                        <X className="w-3.5 h-3.5" /> Revisa
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showFretboard && (
              <div className="flex-1 p-3 min-h-0 overflow-y-auto flex items-start justify-center">
                <div className="w-full max-w-5xl rounded-xl bg-black/15 p-2 border border-white/10">
                  <GuitarNeck
                    highlightedPositions={neckHighlights}
                    mutedStrings={gymMuted}
                    onFretClick={(stringNum, fret) => handleFretClick(stringNum, fret)}
                    showNoteNames={showAnswers}
                    showDegrees={false}
                    showFingerBadges={false}
                    interactive={fretInteractive}
                    hideFretZero={fretRange[0] > 0}
                    practiceFeedback={
                      isCorrect === false ? "fail" : isCorrect === true ? "success" : undefined
                    }
                  />
                </div>
              </div>
            )}

            {showQuizRow && currentStep?.options && (
              <div className="px-4 py-3 bg-black/25 border-t border-white/10 shrink-0">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Selecciona todas las opciones correctas
                </p>
                <div className="grid grid-cols-1 gap-1.5">
                  {currentStep.options.map((option) => {
                    const picked = quizPicked.has(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleQuizOption(option.value)}
                        className={cn(
                          "w-full text-left px-3 py-2.5 rounded-lg border transition-all text-xs font-medium",
                          picked
                            ? "border-[var(--bordona-accent)] bg-[var(--bordona-accent)]/15 text-white"
                            : "border-white/15 bg-white/[0.04] text-slate-200 hover:border-white/25",
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="px-4 py-2.5 bg-black/35 border-t border-white/10 flex items-center justify-between gap-2 shrink-0 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHint((h) => !h)}
                  className={cn(
                    "border-white/15 bg-white/[0.05] text-slate-100",
                    showHint && "bg-amber-500/15 border-amber-400/40",
                  )}
                >
                  <Lightbulb className="w-3.5 h-3.5" />
                  Pista
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReveal}
                  disabled={showAnswers}
                  className="border-white/15 bg-white/[0.05] text-slate-100 disabled:opacity-40"
                >
                  Revelar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRestart}
                  className="text-slate-300 hover:bg-white/10"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reiniciar
                </Button>
              </div>
              {exercise.type === "quiz" && !stepHasQuiz && stepHasFrets && (
                <p className="text-[10px] text-slate-500 max-w-[200px] text-right">
                  Toca el mastil. Varias celdas si el paso lo pide.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function GymExerciseFullLayout() {
  return (
    <div className="flex h-full min-h-0 w-full">
      <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
        <GymExerciseMain />
      </div>
      <div className="w-[min(320px,32vw)] shrink-0 border-l border-white/10 overflow-y-auto min-h-0 p-3 bg-black/25">
        <GymExerciseInspector />
      </div>
    </div>
  );
}

export function GymExerciseInspector() {
  const { exercise, mode, currentStepIdx, currentStep, totalSteps, showHint, setShowHint, progress, sessionElapsedSec } =
    useGymSession();

  return (
    <div className="space-y-3 font-sans text-slate-100">
      {showHint && (
        <div className="p-3 rounded-xl bg-amber-500/15 border border-amber-400/35">
          <div className="flex items-center gap-2 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-200" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-100/90">Pista del profe</span>
          </div>
          <p className="text-xs text-amber-50/95 leading-relaxed">{currentStep?.hint || exercise.professorHint}</p>
        </div>
      )}

      <div className="p-3 rounded-xl bg-sky-500/12 border border-sky-400/25">
        <div className="flex items-center gap-2 mb-1.5">
          <BookOpen className="w-3.5 h-3.5 text-sky-200" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-100/90">Nota del metodo</span>
        </div>
        <p className="text-xs text-slate-100/90 leading-relaxed">{exercise.professorNote}</p>
        <p className="text-[10px] text-sky-200/80 mt-1.5">{exercise.methodRef}</p>
      </div>

      {mode === "playing" && (
        <div className="p-3 rounded-xl bg-white/[0.06] border border-white/10">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Esta sesión</p>
          <p className="text-lg font-bold text-white tabular-nums">
            {Math.floor(sessionElapsedSec / 60)}:{(sessionElapsedSec % 60).toString().padStart(2, "0")}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">Desde que pulsaste Comenzar</p>
        </div>
      )}

      {mode === "playing" && (
        <div className="p-3 rounded-xl bg-white/[0.06] border border-white/10">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Progreso del ejercicio</p>
          <div className="flex gap-1">
            {exercise.steps.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "flex-1 h-1.5 rounded-full",
                  i < currentStepIdx ? "bg-emerald-400" : i === currentStepIdx ? "bg-[var(--bordona-accent)]" : "bg-white/15",
                )}
              />
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            {currentStepIdx + 1} de {totalSteps} pasos
          </p>
        </div>
      )}

      <div className="p-3 rounded-xl bg-white/[0.06] border border-white/10">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Info</p>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Tiempo en el gimnasio</span>
            <span className="font-medium text-slate-200 tabular-nums">
              {formatPracticeDuration(progress.totalPracticeTime)}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Racha</span>
            <span className="font-medium text-orange-200/95 tabular-nums">
              {progress.streakDays > 0 ? `${progress.streakDays} días` : "—"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Tiempo estimado</span>
            <span className="font-medium text-slate-200">~{exercise.timeEstimate} min</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Dificultad</span>
            <span
              className={cn(
                "font-medium",
                exercise.difficulty === "facil" && "text-emerald-300",
                exercise.difficulty === "medio" && "text-sky-300",
                exercise.difficulty === "dificil" && "text-amber-300",
                exercise.difficulty === "experto" && "text-red-300",
              )}
            >
              {getDifficultyLabel(exercise.difficulty)}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Categoria</span>
            <span className="font-medium text-slate-200">{getCategoryLabel(exercise.category)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Tipo</span>
            <span className="font-medium text-slate-200">{getTypeLabel(exercise.type)}</span>
          </div>
        </div>
      </div>

      {!showHint && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-white/15 bg-white/[0.05] text-slate-100"
          onClick={() => setShowHint(true)}
        >
          <Lightbulb className="w-3.5 h-3.5" />
          Ayuda rapida
        </Button>
      )}
    </div>
  );
}
